// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Internal Packages
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";

// External Components
import { MdAddChart, MdRefresh } from "react-icons/md";

// Internal Components
import CustomerCard from "./CustomerCard";
import SearchBar from "../UI/SearchBar";
import DateFilter from "../UI/DateFilter";
import Heading from "../UI/Heading";
import FilterMenu from "../UI/FilterMenu";
import PaginationMenu from "../UI/PaginationMenu";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import DomainSwitch from "../UI/DomainSwitch";
import { BiSolidMegaphone } from "react-icons/bi";

/** ========================================================================
 * * Customers Page
 * Renders a page, which displays all customers.
 * ========================================================================== */

export default function CustomersPage() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    query: "",
    scope: "All",
    booking: "All",
    plan: "",
    screening: "All",
    invoices: "",
    license: "All",
    expired: "All",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key]),
  );

  // Initialize States
  const [customers, setCustomers] = useState([]);
  const [limit, setLimit] = useState(50);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "customer",
        limit,
        filters: filter,
      });

      // Replace URL
      let url = "/customers?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setCustomers(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Booking",
      options: [
        "All",
        { label: "Currently Booked", value: "Booked" },
        { label: "Has Booked", value: "Any" },
        { label: "Currently Not Booked", value: "Not Booked" },
        { label: "Never Booked", value: "None" },
      ],
      selected: filter.booking,
    },
    {
      label: "Plan",
      options: ["LUXURY", "PLUSH", "POSH", "None"],
      selected: filter.plan,
      type: "multiple",
    },
    {
      label: "Screening",
      options: ["All", "Approved", "Declined", "Started"],
      selected: filter.screening,
    },
    {
      label: "Invoices",
      options: ["Paid", "Unpaid", "No Invoices"],
      selected: filter.invoices,
      type: "multiple",
    },
    {
      label: "License",
      options: ["All", "Added", { label: "Not Added", value: "NotAdded" }],
      selected: filter.license,
    },
    {
      label: "Expired",
      options: [
        "All",
        "Expired",
        { label: "Not Expired", value: "NotExpired" },
      ],
      selected: filter.expired,
    },
  ];

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter, limit]);

  return (
    <div className="container grid">
      <Heading title="Customers" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <DomainSwitch
          on={filter.scope !== "All"}
          onSwitch={(scope) => setFilter({ ...filter, scope })}
        />
        <BiSolidMegaphone
          onClick={() => navigate(`/announcements/add${search}table=customer`)}
          className="text-2xl icon"
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
        <ExportCsvButton array={customers} filename="customers" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={8} />
      ) : customers.length > 0 ? (
        <div className="list">
          {customers.map((customer) => (
            <CustomerCard customer={customer} key={customer.id} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={limit}
        setLimit={setLimit}
        loading={loading}
        count={customers.length}
      />
    </div>
  );
}
